import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Box, Typography, CircularProgress, Card, Stack, Chip, Button,
  TextField, Snackbar, Alert,
} from '@mui/material'
import { LocationOn, Schedule, Star, StarBorder, CheckCircle } from '@mui/icons-material'
import axios from 'axios'

interface PublicJobTrack {
  job_reference: string
  job_status: string
  dropoff_address: string
  dropoff_latitude?: number
  dropoff_longitude?: number
  current_lat?: number
  current_lon?: number
  speed?: number
  eta_minutes?: number
  scheduled_at?: string
  brand_color: string
  accent_color: string
  logo_url: string
  welcome_message: string
  allow_reschedule: boolean
  allow_feedback: boolean
}

export default function JobTrackingPage() {
  const { token } = useParams<{ token: string }>()

  const { data, isLoading, isError } = useQuery({
    queryKey: ['public-job-track', token],
    queryFn: () => axios.get<PublicJobTrack>(`/api/v1/public/track/job/${token}`).then((r) => r.data),
    enabled: !!token,
    refetchInterval: 30_000,
  })

  if (isLoading) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f8fafc' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (isError || !data) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: '#f8fafc', gap: 1 }}>
        <Typography variant="h5" fontWeight={700}>Tracking unavailable</Typography>
        <Typography color="#64748b">This link has expired or is invalid.</Typography>
      </Box>
    )
  }

  const isComplete = data.job_status === 'completed'

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', pb: 6 }}>
      {/* Header — branded */}
      <Box sx={{
        bgcolor: data.brand_color, color: '#fff', px: 3, py: 4, textAlign: 'center',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      }}>
        {data.logo_url && <Box component="img" src={data.logo_url} sx={{ height: 40, mb: 1.5 }} />}
        <Typography variant="h5" fontWeight={700}>
          {isComplete ? '✓ Delivered' : 'Tracking your delivery'}
        </Typography>
        {data.welcome_message && (
          <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
            {data.welcome_message}
          </Typography>
        )}
      </Box>

      <Box sx={{ maxWidth: 600, mx: 'auto', mt: 3, px: 2 }}>
        {/* ETA */}
        {!isComplete && data.eta_minutes !== undefined && data.eta_minutes !== null && (
          <Card sx={{ p: 3, mb: 2, textAlign: 'center', borderRadius: 3, border: '1px solid #e2e8f0' }}>
            <Schedule sx={{ fontSize: 36, color: data.brand_color, mb: 1 }} />
            <Typography variant="caption" color="#64748b">Estimated arrival</Typography>
            <Typography variant="h3" fontWeight={700} sx={{ color: data.brand_color }}>
              {Math.round(data.eta_minutes)} min
            </Typography>
          </Card>
        )}

        {/* Job details */}
        <Card sx={{ p: 2.5, mb: 2, borderRadius: 3, border: '1px solid #e2e8f0' }}>
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="caption" color="#94a3b8">REFERENCE</Typography>
              <Typography fontFamily="monospace" fontSize={14}>{data.job_reference || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="#94a3b8">STATUS</Typography>
              <Box>
                <Chip label={data.job_status.replace('_', ' ')} sx={{ textTransform: 'capitalize', bgcolor: data.accent_color + '22', color: data.accent_color, fontWeight: 600 }} />
              </Box>
            </Box>
            {data.dropoff_address && (
              <Box>
                <Typography variant="caption" color="#94a3b8">DROP-OFF</Typography>
                <Stack direction="row" spacing={0.5} alignItems="flex-start">
                  <LocationOn sx={{ fontSize: 18, color: data.brand_color, mt: 0.2 }} />
                  <Typography>{data.dropoff_address}</Typography>
                </Stack>
              </Box>
            )}
            {data.scheduled_at && (
              <Box>
                <Typography variant="caption" color="#94a3b8">SCHEDULED</Typography>
                <Typography>{new Date(data.scheduled_at).toLocaleString()}</Typography>
              </Box>
            )}
          </Stack>
        </Card>

        {/* Feedback */}
        {data.allow_feedback && isComplete && token && (
          <FeedbackCard token={token} brand={data.brand_color} />
        )}
      </Box>
    </Box>
  )
}

function FeedbackCard({ token, brand }: { token: string; brand: string }) {
  const [rating, setRating] = useState(0)
  const [comment, setComment] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')

  const submit = async () => {
    if (rating === 0) return
    try {
      await axios.post('/api/v1/public/feedback', { token, rating, comment })
      setSubmitted(true)
    } catch {
      setError('Could not submit feedback. Please try again.')
    }
  }

  if (submitted) {
    return (
      <Card sx={{ p: 3, textAlign: 'center', borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#f0fdf4' }}>
        <CheckCircle sx={{ fontSize: 36, color: '#10b981', mb: 1 }} />
        <Typography fontWeight={700}>Thanks for your feedback!</Typography>
      </Card>
    )
  }

  return (
    <Card sx={{ p: 2.5, borderRadius: 3, border: '1px solid #e2e8f0' }}>
      <Typography fontWeight={700} sx={{ mb: 1 }}>How was the delivery?</Typography>
      <Stack direction="row" spacing={0.5} sx={{ mb: 2 }}>
        {[1, 2, 3, 4, 5].map((n) => (
          <Box key={n} onClick={() => setRating(n)} sx={{ cursor: 'pointer' }}>
            {n <= rating ? <Star sx={{ fontSize: 32, color: brand }} /> : <StarBorder sx={{ fontSize: 32, color: '#cbd5e1' }} />}
          </Box>
        ))}
      </Stack>
      <TextField
        fullWidth multiline rows={3}
        placeholder="Anything we should know?"
        value={comment} onChange={(e) => setComment(e.target.value)}
      />
      <Button fullWidth variant="contained" sx={{ mt: 1.5, bgcolor: brand, '&:hover': { bgcolor: brand } }}
              disabled={rating === 0} onClick={submit}>
        Submit feedback
      </Button>
      <Snackbar open={!!error} autoHideDuration={3000} onClose={() => setError('')}>
        <Alert severity="error">{error}</Alert>
      </Snackbar>
    </Card>
  )
}
